import Layout from 'components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';

import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import Cards from 'components/cards/cards';
import EmailOnly, { EmailOnlyLayouts } from 'components/email-only/email-only';
import FooterCard from 'components/footer-card/footer-card';
import Quote from 'components/quote/quote';
import Slider from 'components/sticky-slider/sticky-slider';
import SunriseHeader from 'components/sunrise-header/sunrise-header';
import {
  Drupal_FieldNodeIndividualRoleFieldHeaderCta,
  Drupal_NodeIndividualRole,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphQuote,
  Drupal_ParagraphStickySlider
} from 'generated/graphql';
import { useMenuDefaultFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { mqMax, mqMin } from 'theme/base';
import { backgroundGrayPattern } from 'theme/shared';
import { LucidTheme, TemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const Role = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeIndividualRole;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const i18n = i18nFunct(i18nData, i18nKey);
  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeIndividualRoleFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton
  );

  const marketoFormEntity =
    headerCtas.length > 0 && headerCtas[0].fieldMarketo
      ? (headerCtas[0].fieldMarketo.entity as Drupal_ParagraphMarketoForm)
      : null;

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const fullTitle =
    node.fieldTitlePiece && node.fieldTitlePiece.length > 0
      ? (node.fieldTitlePiece as string[])
      : (headerSection?.fieldHeader as string);

  const slides = node
    .fieldStickySlider!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphStickySlider);

  const cards = node
    .fieldHowToSection!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphFullCard);

  const quote = node.fieldQuoteBundle
    ? (node.fieldQuoteBundle.entity as Drupal_ParagraphQuote)
    : null;

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <Navbar menu={navbarMenu} />
      <Box
        as="main"
        css={
          [
            backgroundGrayPattern,
            {
              overflow: 'unset'
            }
          ]
        }
      >
        {headerSection && (
          <SunriseHeader
            breadCrumbHub={node.fieldRoleType!}
            breadCrumbPage={node.fieldTitle!}
            buttons={headerCtas}
            header={fullTitle}
            pageType={pageType}
            subheader={headerSection.fieldSubheader!}
          />
        )}
        <Slider
          buttonText={i18n('mktg_ct_all_get_a_quote')}
          mktoFormEntity={marketoFormEntity}
          sections={slides}
        />
        <Cards cards={cards} />
        {quote && <Quote quote={quote} />}
        <Box
          css={(theme: LucidTheme) => ({
            [mqMin.tabletLandscape]: {
              marginTop: `calc(${theme.space[10]}px + ${theme.space[7]}px)`
            }
          })}
        >
          <FooterCard>
            <Box
              css={(theme: LucidTheme) => ({
                paddingRight: theme.space[5],
                [mqMax.computer]: {
                  paddingRight: theme.space[5]
                },
                [mqMax.tabletSmall]: {
                  padding: theme.space[0],
                  marginBottom: theme.space[5]
                }
              })}
            >
              <Heading as="h2" variant="subheadS">
                {i18n('mktg_ct_consideration_footer_header')}
              </Heading>
            </Box>
            <Box
              css={(theme: LucidTheme) => ({
                [mqMax.tabletSmall]: {
                  padding: theme.space[0]
                }
              })}
            >
              <EmailOnly
                hideOr
                wideBreak
                buttonText={i18n('mktg-nav-sign-up-free')}
                googleButton={i18n('mktg_email_only_or_sign_up_with', {
                  service: 'Google'
                })}
                layout={
                  langPrefix === 'en'
                    ? EmailOnlyLayouts.Cushy
                    : EmailOnlyLayouts.Stacked
                }
              />
            </Box>
          </FooterCard>
        </Box>
      </Box>
      <Footer
        entityTranslations={entityTranslations}
        menu={footerMenu}
      />
    </Layout>
  );
};

export default Role;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeIndividualRole {
          ...entityFields
          fieldTitle
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
              }
            }
          }
          fieldTitlePiece
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonLink
                fieldButtonText
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldHowToSection {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldQuoteBundle {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldAttribution
              }
            }
          }
          fieldIsPrivate
          fieldRoleType
          fieldStickySlider {
            entity {
              ... on Drupal_ParagraphStickySlider {
                fieldHeader
                fieldBody {
                  processed
                }
                fieldImage
                fieldHideImageShadow
                fieldDoNotAddBrow
                fieldCta {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mktg_ct_consideration_footer_header
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
          }
        }
      }
    }
  }
`;
